import { OpenWithEmailButtons } from "@/components/shared/auth/open-with-email-buttons";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { authClient } from "@/lib/auth/auth";
import { FRONTEND_URL } from "@/lib/constants/constants";
import { cn } from "@/lib/utils";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

export const Route = createFileRoute("/otp-code")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    email?: string;
    otp?: string;
    smtpProvider?: string;
    redirectTo?: string;
  };

  const email = search.email ?? "";
  const otpCode = search.otp ?? null;
  const smtpProvider = search.smtpProvider ?? null;

  const [otp, setOtp] = useState(otpCode ?? "");
  const [status, setStatus] = useState<"idle" | "verifying">("idle");
  const [error, setError] = useState<string | null>(null);
  const statusRef = useRef(status);

  let redirectTo = search.redirectTo ?? FRONTEND_URL;

  if (!redirectTo.startsWith(FRONTEND_URL as string)) {
    redirectTo = FRONTEND_URL as string;
  } else {
    const isFrontendUrl =
      new URL(redirectTo).origin === new URL(FRONTEND_URL as string).origin;
    redirectTo = isFrontendUrl ? redirectTo : (FRONTEND_URL as string);
  }

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (!email) {
      setError(t("auth.otp.missing_email"));
    }
  }, [email, t]);

  const verify = useCallback(
    async (value: string) => {
      const code = value.trim();
      if (code.length !== 6) return;

      if (!email) {
        setError(t("auth.otp.missing_email"));
        return;
      }

      if (statusRef.current !== "idle") return;

      setStatus("verifying");
      setError(null);

      try {
        const result = await authClient.signIn.emailOtp(
          {
            email,
            otp: code,
          },
          {
            onSuccess: async () => {
              window.location.href = redirectTo;
            },
          },
        );

        if (result.error) {
          setError(result.error.message ?? t("auth.otp.verification_failed"));
          setStatus("idle");
          return;
        }
      } catch (e) {
        setError(
          e instanceof Error ? e.message : t("auth.otp.verification_failed"),
        );
        setStatus("idle");
      }
    },
    [email, redirectTo, t],
  );

  useEffect(() => {
    if (otp.length < 6) return;
    if (statusRef.current !== "idle") return;
    void verify(otp);
  }, [otp, verify]);

  const handleOtpChange = useCallback(
    (value: string) => {
      if (email && error) {
        setStatus("idle");
        setError(null);
      }
      setOtp(value);
    },
    [email, error],
  );

  const isVerifying = status === "verifying";

  return (
    <div className="flex min-h-screen w-screen flex-col items-center justify-center bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex items-center gap-2 font-medium">
          <img
            src="/logoTabular.png"
            alt="Skipper Logo"
            width="100"
            height="100"
            style={{
              borderRadius: "10%",
            }}
          />
        </div>

        <Card className="w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t("auth.otp.title")}</CardTitle>
            <CardDescription className="space-y-1">
              <Trans
                i18nKey="auth.otp.description"
                values={{ email: email ?? t("auth.otp.your_email") }}
                components={{
                  Bold: <span className={cn(email && "font-semibold")} />,
                }}
              />
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid gap-6">
              <div className="grid gap-4 justify-center">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  pattern={REGEXP_ONLY_DIGITS}
                  onChange={handleOtpChange}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  disabled={isVerifying || !email}
                  aria-label={t("auth.otp.aria_label")}
                  aria-invalid={Boolean(error) || undefined}
                  aria-describedby={error ? "otp-error" : undefined}
                  containerClassName="justify-center"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>

                {error && (
                  <p
                    id="otp-error"
                    className="text-sm text-destructive text-center"
                  >
                    {error}
                  </p>
                )}
                {isVerifying && (
                  <p className="text-sm text-muted-foreground text-center">
                    {t("auth.otp.verifying")}
                  </p>
                )}

                <OpenWithEmailButtons
                  smtpProvider={smtpProvider}
                  isEnabled={Boolean(email)}
                  ctaLabel={t("auth.otp.open_email")}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
